import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '../../redux/userRedux';
import { handleLogout } from '../../utils/auth';
import { getMenuIcon } from './utils/getMenuIcon';
import config from '../../config';
import axios from 'axios';
import {
  HomeOutlined,
  LogoutOutlined,
  DownOutlined,
  RightOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Modal, message } from 'antd';
import './sidebar.css';
import logo from './../../assets/falcon.png';

const Sidebar = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector((state) => state.user.currentUser.id);
  const isSidebarOpen = useSelector((state) => state.user?.isSidebarOpen);

  const [data, setData] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);

  const currentPath = location.pathname;

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      dispatch(toggleSidebar());
    }
  };

  const Logout = () => {
    Modal.confirm({
      title: 'Déconnexion',
      content: 'Êtes-vous sûr de vouloir vous déconnecter ?',
      okText: 'Oui, me déconnecter',
      cancelText: 'Annuler',
      okButtonProps: { danger: true },
      icon: <ExclamationCircleOutlined />,
      onOk: () => handleLogout(navigate),
    });
  };

  const toggleSubMenu = (menuId) => {
    setOpenKeys((prev) =>
      prev.includes(menuId) ? prev.filter((k) => k !== menuId) : [menuId]
    );
  };

  const fetchMenu = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${DOMAIN}/menu/sidebar`
      );
      const seen = new Set();
      const deduped = data.filter((m) => {
        if (seen.has(m.menu_id)) return false;
        seen.add(m.menu_id);
        return true;
      });
      setData(deduped);
    } catch (error) {
      console.error('Erreur chargement menu:', error);
      message.error('Erreur de chargement du menu');
    }
  }, [DOMAIN, userId]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  const isActiveParent = useMemo(() => {
    for (const menu of data) {
      if (menu.subMenus && menu.subMenus.length > 0) {
        for (const sub of menu.subMenus) {
          if (currentPath === sub.submenu_url || currentPath.startsWith(sub.submenu_url + '/')) {
            return menu.menu_id;
          }
        }
      }
    }
    return null;
  }, [data, currentPath]);

  useEffect(() => {
    if (isActiveParent && !openKeys.includes(isActiveParent)) {
      setOpenKeys((prev) => [...prev, isActiveParent]);
    }
  }, [isActiveParent]);

  const closeOnOverlay = (e) => {
    if (e.target === e.currentTarget) {
      dispatch(toggleSidebar());
    }
  };

  const isHome = currentPath === '/';

  return (
    <>
      <div className="sb__overlay" onClick={closeOnOverlay} />
      <aside className={"sb " + (isSidebarOpen ? 'visible' : '')}>
      <div className="sb__brand">
        <img src={logo} className="sb__logo" alt="Falcon" />
      </div>

      <nav className="sb__nav">
        <Link
          to={'/'}
          className={"sb__item " + (isHome ? 'sb__item--active' : '')}
          onClick={handleLinkClick}
        >
          <span className="sb__item-icon">
            <HomeOutlined />
          </span>
          <span className="sb__item-label">Accueil</span>
        </Link>

        <div className="sb__divider" />

        {data.map((menuItem) => {
          const isOpen = openKeys.includes(menuItem.menu_id);
          const hasSubs = menuItem.subMenus && menuItem.subMenus.length > 0;
          const isActive = isActiveParent === menuItem.menu_id;

          return (
            <div key={menuItem.menu_id} className="sb__group">
              <button
                type="button"
                className={
                  "sb__item sb__item--parent " +
                  (isOpen ? 'sb__item--open ' : '') +
                  (isActive ? 'sb__item--active-parent' : '')
                }
                onClick={() => hasSubs && toggleSubMenu(menuItem.menu_id)}
              >
                <span className="sb__item-icon">
                  {getMenuIcon(menuItem.menu_icon)}
                </span>
                <span className="sb__item-label">{menuItem.menu_title}</span>
                {hasSubs && (
                  <span className="sb__item-chevron">
                    {isOpen ? <DownOutlined /> : <RightOutlined />}
                  </span>
                )}
              </button>

              {hasSubs && (
                <div className={"sb__submenu " + (isOpen ? 'sb__submenu--open' : '')}>
                  {menuItem.subMenus.map((sub) => {
                    const subActive = currentPath === sub.submenu_url || currentPath.startsWith(sub.submenu_url + '/');
                    return (
                      <Link
                        key={sub.submenu_id}
                        to={sub.submenu_url}
                        className={"sb__subitem " + (subActive ? 'sb__subitem--active' : '')}
                        onClick={handleLinkClick}
                      >
                        <span className="sb__subitem-dot" />
                        <span className="sb__subitem-label">{sub.submenu_title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="sb__footer">
        <div className="sb__divider" />
        <button type="button" className="sb__logout" onClick={Logout}>
          <span className="sb__logout-icon">
            <LogoutOutlined />
          </span>
          <span className="sb__logout-label">Deconnexion</span>
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
